import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingCart, Search, Menu, X, MapPin } from "lucide-react";
import { LOGOS } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const NAV = [
  { to: "/shop", label: "Shop", id: "nav-shop-link" },
  { to: "/new-releases", label: "New Releases", id: "nav-new-releases-link" },
  { to: "/events", label: "Events", id: "nav-events-link" },
  { to: "/about", label: "About", id: "nav-about-link" },
  { to: "/contact", label: "Contact", id: "nav-contact-link" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");
  const { count } = useCart();
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
    setMobileOpen(false);
  };

  return (
    <header
      className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl"
      data-testid="site-header"
    >
      {/* Announcement bar */}
      <div className="bg-brand-navy-dark text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-[11px] font-semibold tracking-wider sm:px-6 lg:px-8">
          <span className="uppercase text-white/80">Free shipping on orders $50+</span>
          <span className="hidden items-center gap-1.5 uppercase text-white/70 sm:flex">
            <MapPin size={12} className="text-brand-teal" /> Visit us in-store — 7 days a week
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
            <img
              src={LOGOS.full}
              alt="Elevation Gaming Co."
              className="hidden h-11 w-auto md:block"
            />
            <img
              src={LOGOS.shield}
              alt="Elevation Gaming Co."
              className="h-10 w-auto md:hidden"
            />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.id}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-semibold tracking-tight transition-colors ${
                    isActive
                      ? "text-brand-navy bg-brand-teal/10"
                      : "text-slate-700 hover:text-brand-navy hover:bg-slate-100"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <form
            onSubmit={submitSearch}
            className="relative hidden max-w-xs flex-1 md:block"
            data-testid="header-search-form"
          >
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Pokémon, MTG, dice…"
              className="h-10 w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 text-sm outline-none transition focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30"
              data-testid="header-search-input"
            />
          </form>

          <div className="flex items-center gap-2">
            <Link
              to="/cart"
              className="relative inline-flex h-11 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-brand-navy transition hover:border-brand-teal hover:text-brand-navy-dark"
              data-testid="header-cart-link"
              aria-label={`Cart, ${count} items`}
            >
              <ShoppingCart size={18} />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span
                  className="absolute -right-1 -top-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-teal px-1.5 text-[11px] font-bold text-white"
                  data-testid="header-cart-count"
                >
                  {count}
                </span>
              )}
            </Link>

            <button
              type="button"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-brand-navy lg:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-slate-200 bg-white lg:hidden" data-testid="mobile-menu">
          <div className="mx-auto max-w-7xl space-y-3 px-4 py-4 sm:px-6">
            <form onSubmit={submitSearch} className="relative">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products…"
                className="h-11 w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 text-sm outline-none focus:border-brand-teal"
                data-testid="mobile-search-input"
              />
            </form>
            <nav className="flex flex-col gap-1" aria-label="Mobile primary">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`${n.id}-mobile`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                      isActive ? "bg-brand-teal/10 text-brand-navy" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
